import "./App.css";
import { useEffect, useState } from "react";
import {
  BrowserRouter,
  HashRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { LoaderCircle } from "lucide-react";
import { AuthProvider } from "./context/AuthContext";
import Header from "./components/Header";
import Home from "./pages/Home";
import RoutesList from "./pages/RoutesList";
import RouteDetail from "./pages/RouteDetail";
import Admin from "./pages/Admin";
import Favorites from "./pages/Favorites";
import About from "./pages/About";
import Explore from "./pages/Explore";
import AuthCallback from "./pages/AuthCallback";
import CreatorProfile from "./pages/CreatorProfile";
import CreatorStudio from "./pages/CreatorStudio";
import { Toaster } from "./components/ui/sonner";
import { api } from "./lib/api";

function BackendLoadingScreen() {
  return (
    <div
      className="backend-loading-screen"
      role="status"
      aria-live="polite"
      aria-label="Connecting to backend"
    >
      <div className="backend-loading-card">
        <LoaderCircle className="backend-loading-icon" aria-hidden="true" />
        <p>Starting backend...</p>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id synchronously during render to avoid race conditions
  if (
    location.hash?.includes("session_id=") ||
    location.pathname === "/auth/callback"
  ) {
    return <AuthCallback />;
  }

  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/routes" element={<RoutesList />} />
        <Route path="/routes/:id" element={<RouteDetail />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/about" element={<About />} />
        <Route path="/creator" element={<CreatorProfile />} />
        <Route path="/creator/studio" element={<CreatorStudio />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* Catch-all: allow client-side router to handle unknown paths (direct links) */}
        <Route path="*" element={<Home />} />
      </Routes>
    </>
  );
}

function BackendGate({ children }) {
  const [backendReady, setBackendReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const checkBackend = async () => {
      try {
        await api.get("/");
        if (!cancelled) {
          setBackendReady(true);
        }
      } catch {
        if (!cancelled) {
          retryTimer = window.setTimeout(checkBackend, 2000);
        }
      }
    };

    checkBackend();

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, []);

  if (!backendReady) {
    return <BackendLoadingScreen />;
  }

  return children;
}

function App() {
  // Use HashRouter as a fallback when server-side SPA fallback is not available.
  // Set `REACT_APP_USE_HASH_ROUTER=true` in your environment to enable.
  const useHash = String(process.env.REACT_APP_USE_HASH_ROUTER) === "true";
  const Router = useHash ? HashRouter : BrowserRouter;

  return (
    <div className="App">
      <Router>
        <BackendGate>
          <AuthProvider>
            <AppRouter />
            <Toaster position="bottom-right" />
          </AuthProvider>
        </BackendGate>
      </Router>
    </div>
  );
}

export default App;
